import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { To, useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../../../context/ToastContext';
import {
  useActivateRegistrationMutation,
  useDeleteRegistrationMutation,
  useGetRegistrationQuery
} from '../../../../../services/endpoints/content/registrations';
import { formatToCurrency, getErrorMessage } from '../../../../../utils/utils';
import { useEffect, useState } from 'react';

export const RegistrationsDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetRegistrationQuery(+params.id!, {
    refetchOnMountOrArgChange: true
  });
  const [deleteRegistration, { isLoading: isDeleting }] = useDeleteRegistrationMutation();
  const [activateRegistration, { isLoading: isActivating }] =
    useActivateRegistrationMutation();
  const [showModal, setShowModal] = useState(false);
  const registration = data?.registration;
  const isInactive = registration?.active_status === 'inactive';
  const statusAction = isInactive ? 'Reactivate' : 'Deactivate';

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading registration', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  const handleStatusChange = async () => {
    try {
      const id = +params.id!;
      if (isInactive) {
        await activateRegistration(id).unwrap();
      } else {
        await deleteRegistration(id).unwrap();
      }
      addToast(`Registration ${statusAction.toLowerCase()}d successfully`, 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !registration) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <div>Loading...</div>
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate(-1 as To)}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Registrations List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Registration details#${registration.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => navigate(`/admin/registrations/${registration.id}/edit`)}
          >
            Edit Registration
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowModal(true)}
          >
            {statusAction} Registration
          </SimpleButton>
        </Stack>
      </div>
      <div className="d-lg-none d-flex gap-2 px-4 py-3 border-bottom">
        <SimpleButton
          variant="primary"
          height={36}
          onClick={() => navigate(`/admin/registrations/${registration.id}/edit`)}
        >
          Edit
        </SimpleButton>
        <SimpleButton
          variant="primary"
          height={36}
          onClick={() => setShowModal(true)}
        >
          {statusAction}
        </SimpleButton>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Refund:"
              text={registration.id}
            />
            <LabelText
              label="Order:"
              children={
                <LinkCell
                  url={`/admin/orders/orders/${registration.order?.id}`}
                  content={`Order# ${registration.order_id}`}
                />
              }
            />
            <LabelText
              label="Child:"
              children={
                <LinkCell
                  url={`/admin/sfc-users/children/${registration.child?.id}`}
                  content={`${registration.child?.full_name}`}
                />
              }
            ></LabelText>
            <LabelText
              label="Session:"
              children={
                <LinkCell
                  url={`/admin/schedule/sessions/${registration.course?.id}`}
                  content={`${registration.course?.title}`}
                />
              }
            ></LabelText>
            <LabelText
              label="Session Cost:"
              text={`${formatToCurrency(registration.price)}`}
            ></LabelText>
            <LabelText
              label="Status:"
              text={registration.active_status === 'active' ? 'Active' : 'Inactive'}
            />
            <LabelText
              label="Scolarship:"
              text={`${registration.order?.discount?.scholarship?.scholarship_type ?? 'No'}`}
            ></LabelText>
          </DetailSection>
        </Stack>
      </div>

      <ConfirmationModal
        show={showModal}
        title={`${statusAction} Registration`}
        message={`Are you sure you want to ${statusAction.toLowerCase()} this registration?`}
        confirmText={`Yes, ${statusAction}`}
        cancelText="Cancel"
        onConfirm={handleStatusChange}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting || isActivating}
      />
    </>
  );
};
